import { AddressService } from '@/common/services/address/address.service';
import { UrlService } from '@/common/services/url/url.service';
import { ValidationService } from '@/common/services/validation/validation.service';
import { isValid } from '@/common/utils/validation/common.utils';
import { GetCitiesUseCase } from '@/core/domain/address/usecases/getCities.usecase';
import { GetNeighborhoodsUseCase } from '@/core/domain/address/usecases/getNeighborhoods.usecase';
import { GetStatesUseCase } from '@/core/domain/address/usecases/getSates.usecase';
import { BillingInfo } from '@/core/domain/billing/models/billing-body.model';
import { SendUserDataUseCase } from '@/core/domain/billing/usecases/sendUserData.usecase';
import { StoreBillingDataUseCase } from '@/core/domain/billing/usecases/storeBilling.usecase';
import { clearPrecheckin } from '@/core/reservation/store/precheckin.actions';
import { selectPrecheckin } from '@/core/reservation/store/precheckin.selector';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import Swal from 'sweetalert2';

@Component({
    selector: 'fe-billing-data',
    templateUrl: './billing-data.component.html',
    styleUrls: ['./billing-data.component.scss'],
})
export class BillingDataComponent implements OnInit, OnChanges {
    @Input() submitForm = false;
    @Output() loadingStateChange = new EventEmitter<boolean>();

    billingOption = 'no';
    _addressOption = 'sameAddress';
    lastNameUser = '';

    waiting = false;

    billingForm!: FormGroup;
    addressForm!: FormGroup;

    public loadingStates = false;
    public loadingCities = false;
    public loadingNeighborhoods = false;
    public loadingZipCodes = false;

    public disabledZipCode = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private fb: FormBuilder,
        private readonly validation: ValidationService,
        private readonly getStatesUseCase: GetStatesUseCase,
        private readonly getCitiesUseCase: GetCitiesUseCase,
        private readonly getNeighborhoodsUseCase: GetNeighborhoodsUseCase,
        private storeBillingDataUseCase: StoreBillingDataUseCase,
        private sendUserDataUseCase: SendUserDataUseCase,
        public addressService: AddressService,
        private url: UrlService,
        private store: Store,
        private readonly translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.billingForm = this.fb.group({
            rfc: ['', [Validators.required, this.validation.rfcValidator()]],
            businessName: ['', [Validators.required]],
            email: [
                '',
                [
                    Validators.required,
                    Validators.compose([
                        Validators.required,
                        this.validation.validEmail,
                    ]),
                ],
            ],
        });

        this.addressForm = this.fb.group({
            street: ['', [Validators.required]],
            number: ['', [Validators.required]],
            state: ['', [Validators.required]],
            city: ['', [Validators.required]],
            neighborhood: ['', [Validators.required]],
            postalCode: ['', [Validators.required, Validators.minLength(5)]],
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['submitForm'] && changes['submitForm'].currentValue) {
            this.onSubmit();
        }
    }

    set addressOption(value: string) {
        this._addressOption = value;
        if (value === 'differentAddress') {
            this.getStates();
        }
    }

    get addressOption(): string {
        return this._addressOption;
    }

    private getStates(): void {
        this.loadingStates = true;
        this.getStatesUseCase
            .execute(155)
            .subscribe(() => (this.loadingStates = false));
    }

    public onChangeState(): void {
        const stateId = _.first(this.addressForm.get('state')?.value) as number;

        this.addressService.cities = [];
        this.addressService.neighborhoods = [];
        this.addressService.zipCodes = [];
        this.addressForm.get('city')?.setValue(null);
        this.addressForm.get('neighborhood')?.setValue(null);
        this.addressForm.get('postalCode')?.setValue(null);

        if (!isValid(stateId)) {
            return;
        }

        this.getCities(stateId);
    }

    private getCities(stateId: number): void {
        this.loadingCities = true;
        this.getCitiesUseCase
            .execute(stateId)
            .subscribe(() => (this.loadingCities = false));
    }

    public onChangeCity(): void {
        const cityId = _.first(this.addressForm.get('city')?.value) as number;

        this.addressService.neighborhoods = [];
        this.addressService.zipCodes = [];
        this.addressForm.get('neighborhood')?.setValue(null);
        this.addressForm.get('postalCode')?.setValue(null);

        if (!isValid(cityId)) {
            return;
        }

        this.getNeighborhoods(cityId);
    }

    private getNeighborhoods(cityId: number): void {
        this.loadingNeighborhoods = true;
        this.getNeighborhoodsUseCase
            .execute(cityId)
            .subscribe(() => (this.loadingNeighborhoods = false));
    }

    public onChangeNeighborhood(): void {
        const neighborhoodId = _.first(
            this.addressForm.get('neighborhood')?.value,
        ) as number;

        if (!isValid(neighborhoodId)) {
            this.disabledZipCode = false;
            this.addressService.zipCode = null;
            this.addressForm.get('postalCode')?.setValue(null);
            return;
        }

        this.disabledZipCode = true;

        this.addressForm
            .get('postalCode')
            ?.setValue(this.addressService.getZipCode(neighborhoodId));
    }

    private mapFormToBillingInfo(): BillingInfo {
        const billingFormValues = this.billingForm.value;
        const addressFormValues = this.addressForm.value;

        const billingInfo: BillingInfo = {
            requiredBilling: this.billingOption !== 'no',
            differentAddress: this.addressOption === 'differentAddress',
        };

        if (this.billingOption !== 'no') {
            billingInfo.billing = {
                rfc: billingFormValues.rfc,
                companyName: billingFormValues.businessName,
                email: billingFormValues.email,
            };

            if (this.addressOption === 'differentAddress') {
                billingInfo.billingAddress = {
                    street: addressFormValues.street,
                    cityId: addressFormValues.city[0],
                    stateId: addressFormValues.state[0],
                    suburbId: addressFormValues.neighborhood[0],
                    zipCode: addressFormValues.postalCode,
                    buildingNumber: addressFormValues.number,
                };
            }
        }

        return billingInfo;
    }

    onSubmit(): void {
        if (!this.waiting && this.isFormValid()) {
            this.waiting = true;
            this.loadingStateChange.emit(this.waiting);
            const billingInfo = this.mapFormToBillingInfo();
            this.storeBillingDataUseCase
                .execute(billingInfo)
                .subscribe((result) => {
                    if (result === true) {
                        const reservationCode =
                            this.route.snapshot.paramMap.get('resevationCode');
                        if (reservationCode) {
                            this.store
                                .select(selectPrecheckin)
                                .subscribe((response) => {
                                    this.lastNameUser = response.lastName;
                                });
                            this.sendUserDataUseCase.execute().subscribe({
                                next: () => {
                                    let redirectUrl = this.url.format(
                                        'reservations/reservation-information',
                                    );

                                    redirectUrl = [redirectUrl].join('/');

                                    this.router.navigate([redirectUrl], {
                                        queryParams: {
                                            reservationCode: reservationCode,
                                            lastName: this.lastNameUser,
                                        },
                                    });
                                    this.store.dispatch(clearPrecheckin());
                                    this.waiting = false;
                                    this.loadingStateChange.emit(this.waiting);
                                },
                                error: () => {
                                    const errorMessage =
                                        this.translate.instant(
                                            'fe_billing_data_error_generic',
                                        );

                                    Swal.fire({
                                        icon: 'error',
                                        title: 'Error',
                                        text: errorMessage,
                                    });

                                    this.waiting = false;
                                    this.loadingStateChange.emit(this.waiting);
                                },
                            });
                        } else {
                            this.waiting = false;
                            this.loadingStateChange.emit(this.waiting);
                        }
                    } else {
                        this.waiting = false;
                        this.loadingStateChange.emit(this.waiting);
                    }
                });
        } else {
            this.markFormsAsTouched();
        }
    }

    private isFormValid(): boolean {
        if (this.billingOption === 'no') {
            return true;
        }

        const billingFormValid = this.billingForm.valid;
        const addressFormValid =
            this.addressOption === 'sameAddress' || this.addressForm.valid;

        return billingFormValid && addressFormValid;
    }

    private markFormsAsTouched(): void {
        this.billingForm.markAllAsTouched();

        if (this.addressOption === 'differentAddress') {
            this.addressForm.markAllAsTouched();
        }
    }
}
